// src/components/FilterBar.tsx
import * as React from "react"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

export interface FilterOption {
  id: string
  label: string
}

export interface FilterBarProps {
  filters: FilterOption[]
  active: string
  onChange: (id: string) => void
  search: string
  onSearch: (value: string) => void
  placeholder?: string
  variant?: "pills" | "tabs"
  counts?: Record<string, number>
}

export function FilterBar({
  filters,
  active,
  onChange,
  search,
  onSearch,
  placeholder,
  variant = "pills",
  counts,
}: FilterBarProps) {
  const [internalSearch, setInternalSearch] = React.useState(search)
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync internal search state when controlled `search` prop changes externally
  React.useEffect(() => {
    setInternalSearch(search)
  }, [search])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInternalSearch(value)

    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }
    debounceRef.current = setTimeout(() => {
      onSearch(value)
    }, 300)
  }

  // Clean up debounce on unmount
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [])

  if (variant === "tabs") {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-1 border-b border-line overflow-x-auto">
          {filters.map((filter) => {
            const isActive = active === filter.id
            const count = counts?.[filter.id]
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => onChange(filter.id)}
                className={cn(
                  "flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors -mb-px",
                  isActive
                    ? "text-ink border-b-2 border-primary"
                    : "text-muted hover:text-ink border-b-2 border-transparent",
                )}
              >
                {filter.label}
                {count !== undefined && (
                  <span
                    className={cn(
                      "font-mono text-[11px] font-semibold",
                      isActive ? "text-primary" : "text-muted-2",
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        <Input
          type="search"
          value={internalSearch}
          onChange={handleSearchChange}
          placeholder={placeholder ?? "Suchen..."}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {filters.map((filter) => (
          <button
            key={filter.id}
            type="button"
            onClick={() => onChange(filter.id)}
            className={cn(
              "flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors",
              active === filter.id
                ? "bg-primary text-primary-foreground"
                : "bg-background text-muted-foreground border border-input hover:bg-accent hover:text-accent-foreground"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>
      <Input
        type="search"
        value={internalSearch}
        onChange={handleSearchChange}
        placeholder={placeholder ?? "Suchen..."}
      />
    </div>
  )
}
